import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, useMemo } from 'react';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import InputFieldBadge from '../../components/CommonComps/Badges/InputFieldBadge';
import AcademiaButton from '../../components/CommonComps/Buttons/AcademiaButton';
import AcademiaCheckboxField from '../../components/CommonComps/Inputs/AcademiaCheckboxField';
import AcademiaFormSelect from '../../components/CommonComps/Inputs/AcademiaFormSelect';
import AcademiaInputField from '../../components/CommonComps/Inputs/AcademiaInputField';
import BadgeLoading from '../../components/CommonComps/Loadings/BadgeLoading';
import PasswordRestrictionRules from '../../components/CommonComps/PasswordRestrictionRules';
import PageTitle from '../../components/CommonComps/Titles/PageTitle';
import {
  SELECT_FIELD_DEFAULT_VALUE,
  USERNAME_REGEX,
} from '../../constants/settings';
import { useApplicationContext } from '../../contexts/ApplicationProvider';
import useApplicationActions from '../../hooks/APIHooks/users/useApplicationActions';
import useCheckActions from '../../hooks/APIHooks/users/useCheckActions';
import useRolesActions from '../../hooks/APIHooks/users/useRolesActions';
import { createApplicationSchema } from '../../schemas';
import { ApplicationReqBody } from '../../types/applications';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';

const Application = () => {
  // integration of context hooks here
  const { username, setUsername } = useApplicationContext();

  // integration of custom API hooks here
  const { createNewApplicationHandler } = useApplicationActions();
  const { getCheckAndSuggestUsername } = useCheckActions(username);
  const { getRoleListHandler } = useRolesActions('roleList');

  // integration of react router dom hooks here
  const navigate = useNavigate();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting role options here
  const roleOptions = useMemo(() => {
    if (!getRoleListHandler.data?.is_success) return [];

    return getRoleListHandler.data.data
      .filter((role) => role.role_name.toLowerCase().includes('associate'))
      .map((role) => ({
        id: role.id,
        name: role.role_name,
      }));
  }, [getRoleListHandler.data]);

  // handler function to submit application form declared here
  const submitApplicationHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log('APPLICATION FORM VALUES: ', values);

      const body: ApplicationReqBody = {
        user: {
          username: username.toLowerCase(),
          email: values.email,
          role: values.role.id,
          password: values.password,
          created_by: 'Anonymous',
        },
        user_profile: {
          phone: null,
        },
      };

      console.log('APPLICATION BODY: ', body);

      await createNewApplicationHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Account Created!! Now, login to your account.');

            actions.resetForm();
            setUsername('');

            navigate('/');
          } else {
            toast.error(res.message || 'Application Submission Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      console.log(error);
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering application page component here
  return (
    <main className='font-lato w-[95%] md:w-full mx-auto min-h-screen overflow-x-hidden'>
      <PageTitle title='Application' />

      <section className='grid grid-cols-1 xl:grid-cols-2 min-h-screen'>
        <motion.div
          initial={{ x: -500, opacity: 0 }}
          animate={{
            x: 0,
            opacity: 1,
            transition: {
              ease: 'easeInOut',
              duration: 0.4,
            },
          }}
          className='relative hidden xl:block m-4 rounded-xl overflow-hidden'
        >
          <img
            className='h-full object-cover'
            src='/assets/images/lawyer_suit.jpg'
            alt='lawyer in suit'
          />

          <div className='absolute flex items-center p-6 inset-0 bg-black/40 size-full'>
            <h1 className='text-7xl 2xl:text-8xl font-bold text-white'>
              For professionals
              <br /> shaping the <br />
              <span className='text-legal-brown font-noto italic'>
                future of law.
              </span>
            </h1>
          </div>
        </motion.div>

        <motion.div
          initial={{ x: 500, opacity: 0 }}
          animate={{
            x: 0,
            opacity: 1,
            transition: {
              ease: 'easeInOut',
              duration: 0.4,
            },
          }}
          className='md:w-2/3 2xl:w-1/2 mx-auto my-auto py-10 2xl:py-6'
        >
          <div className='mb-6 text-center md:text-left'>
            <h1 className='text-3xl 2xl:text-4xl italic text-legal-dark-blue font-bold'>
              Associate's Application
            </h1>

            <p className='text-slate-500'>
              Create your account to join our exclusive associate network.
            </p>
          </div>

          <Formik
            initialValues={{
              username: username.toLowerCase(),
              email: '',
              password: '',
              confirm_password: '',
              role: SELECT_FIELD_DEFAULT_VALUE,
              is_agree: false,
            }}
            validationSchema={createApplicationSchema}
            onSubmit={submitApplicationHandler}
          >
            {(props) => (
              <Form className='space-y-4'>
                <AcademiaFormSelect
                  name='role'
                  label={`Select Applicant's Role`}
                  options={roleOptions}
                  defaultValue={props.values.role}
                  setValueOnChange={(role) => props.setFieldValue('role', role)}
                  showSearchInput
                  isDisabled={!roleOptions.length}
                  isRequired
                />

                <div>
                  <AcademiaInputField
                    type='text'
                    name='username'
                    label={`Applicant's Username`}
                    placeholder='Enter your username here...'
                    value={username}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setUsername(e.target.value);
                      props.setFieldValue('username', e.target.value);

                      if (USERNAME_REGEX.test(e.target.value)) {
                        queryClient.invalidateQueries({
                          queryKey: [
                            'check-and-suggest-username',
                            e.target.value,
                          ],
                        });
                      }
                    }}
                    isRequired
                    badge={
                      getCheckAndSuggestUsername.isLoading ? (
                        <BadgeLoading />
                      ) : getCheckAndSuggestUsername.data &&
                        props.values.username ? (
                        <InputFieldBadge
                          badgeText=''
                          badgeValue={getCheckAndSuggestUsername.data.message}
                          isValid={
                            getCheckAndSuggestUsername.data.message ===
                            'Available'
                          }
                        />
                      ) : null
                    }
                  />

                  <AnimatePresence
                    initial={false}
                    mode='wait'
                    onExitComplete={() => null}
                  >
                    {getCheckAndSuggestUsername.data?.data ? (
                      <motion.label
                        initial={{ y: -20, opacity: 0, display: 'none' }}
                        animate={
                          getCheckAndSuggestUsername.data.message ===
                          'Already Taken'
                            ? { y: 0, opacity: 1, display: 'block' }
                            : {
                                y: -20,
                                opacity: 0,
                                transitionEnd: { display: 'none' },
                              }
                        }
                        htmlFor={`input-user`}
                        className='block mt-1 text-base-red text-sm font-light'
                      >
                        Suggested:
                        {getCheckAndSuggestUsername?.data?.data.map(
                          (suggestion, index) => (
                            <button
                              key={index}
                              type='button'
                              onClick={() => {
                                setUsername(suggestion);
                                props.setFieldValue('username', suggestion);
                              }}
                              className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                            >
                              <span className='underline'>{suggestion}</span>
                              {index <
                              (getCheckAndSuggestUsername?.data?.data
                                ?.length as number) -
                                1
                                ? ', '
                                : ''}
                            </button>
                          )
                        )}
                        etc.
                      </motion.label>
                    ) : null}
                  </AnimatePresence>
                </div>

                <AcademiaInputField
                  type='email'
                  name='email'
                  label={`Applicant's Email`}
                  placeholder='Enter your email here...'
                  isRequired
                />

                <div>
                  <AcademiaInputField
                    type='password'
                    name='password'
                    label='Password'
                    placeholder='Enter password here...'
                    isRequired
                  />

                  <PasswordRestrictionRules
                    password={props.values.password}
                    isTouched={!!props.touched.password}
                    error={props.errors.password}
                  />
                </div>

                <AcademiaInputField
                  type='password'
                  name='confirm_password'
                  label='Confirm Password'
                  placeholder='Retype password to confirm here...'
                  isRequired
                />

                <AcademiaCheckboxField
                  name='is_agree'
                  label={
                    <>
                      By creating an account, you agree to the{' '}
                      <Link
                        to={'#'}
                        className='text-legal-brown underline italic'
                      >
                        Terms & conditions
                      </Link>{' '}
                      and{' '}
                      <Link
                        to={'#'}
                        className='text-legal-brown underline italic'
                      >
                        Privacy Policy
                      </Link>{' '}
                      of MyLegalites
                    </>
                  }
                  isRequired
                />

                <AcademiaButton
                  title={
                    props.values.role.id === -1
                      ? 'Please, select a role first!'
                      : undefined
                  }
                  type='submit'
                  variant='premium'
                  isLoading={props.isSubmitting}
                  icon={<IoMdCheckmarkCircle className='size-6' />}
                  additionalClassNames='w-full'
                  isDisabled={props.values.role.id === -1}
                >
                  Create Account
                </AcademiaButton>
              </Form>
            )}
          </Formik>
        </motion.div>
      </section>
    </main>
  );
};

export default Application;
